import * as Sentry from "@sentry/nextjs";

import type {
  ScopeContext,
  SeverityLevel,
  TransactionEvent,
} from "@sentry/types";

import { PostgrestError } from "@supabase/supabase-js";
import { SentryFeatureTags } from "./types";

export class NamedError extends Error {
  constructor(name: string, message: string) {
    super(message);
    this.name = name;
  }
}

export type OurSentryAdditionalData = Omit<Partial<ScopeContext>, "tags"> & {
  tags?: Record<string, any> & {
    feature?: SentryFeatureTags;
    supabase?: boolean;
  };
};

export type OurSentryAdditionalDataPlus =
  | OurSentryAdditionalData
  | {
      data: string;
    };

export function logSupabaseException(
  error: PostgrestError,
  what: string,
  additionalData?: OurSentryAdditionalData
) {
  logException(new NamedError(what, error.message), {
    ...additionalData,
    tags: {
      ...(additionalData?.tags || {}),
      supabase: true,
    },
  });
}

/**
 * `{ data: "..." }` is legacy sugar for `{ extra: { data: "..." } }`, used by
 * hundreds of call sites. Passed through untouched it never reaches the event:
 * alone, the object carries no ScopeContext key, so the SDK parses it as an
 * EventHint (where `data` only feeds internal __sentry__/mechanism checks);
 * alongside `tags`/`extra` it parses as a ScopeContext, whose `Scope.update`
 * destructures `data` away. Fold it into `extra` before capture — never into the
 * message or fingerprint, since these strings interpolate IDs and would split
 * one issue into thousands.
 */
function withDataAsExtra(
  additionalData?: OurSentryAdditionalDataPlus
): OurSentryAdditionalData | undefined {
  if (!additionalData || !("data" in additionalData)) {
    return additionalData as OurSentryAdditionalData | undefined;
  }

  const { data, ...rest } = additionalData as OurSentryAdditionalData & {
    data: string;
  };

  return { ...rest, extra: { ...rest.extra, data } };
}

function parseBoolean(value: string | undefined): boolean {
  return value === "true" || value === "1";
}

export const logException = (
  exception: any,
  additionalData?: OurSentryAdditionalDataPlus
) => {
  if (
    parseBoolean(process.env.IS_DEV) &&
    process.env.NEXT_PUBLIC_ALLOW_SENTRY_LOGGING_ON_DEV !== "1"
  ) {
    console.log(
      "[SENTRY] Capturing exception:",
      exception,
      additionalData ?? ""
    );
    return;
  }

  Sentry.captureException(exception, withDataAsExtra(additionalData));
};

class ValidationError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "ValidationError";
  }
}

export const logValidationError = (
  exception: string,
  additionalData?: OurSentryAdditionalDataPlus
) => {
  if (
    parseBoolean(process.env.IS_DEV) &&
    process.env.NEXT_PUBLIC_ALLOW_SENTRY_LOGGING_ON_DEV !== "1"
  ) {
    console.log(
      "[SENTRY] Capturing validation error:",
      exception,
      additionalData ?? ""
    );
    return;
  }

  const error = new ValidationError(exception);
  const stack = error.stack?.split("\n");
  if (stack) {
    error.stack = stack[0] + stack.slice(1).join("\n");
  }

  Sentry.captureException(error, withDataAsExtra(additionalData));
};

export const logMessage = (
  message: string,
  additionalData?: OurSentryAdditionalData | SeverityLevel
) => {
  if (
    parseBoolean(process.env.IS_DEV) &&
    process.env.ALLOW_SENTRY_LOGGING_ON_DEV !== "1"
  ) {
    console.log("[SENTRY] Capturing message:", message, additionalData ?? "");
    return;
  }
  Sentry.captureMessage(message, additionalData);
};

type RunFunction<T> = () => Promise<T>;

export const runFunctionWithSpan = async <T>(
  name: string,
  op: string,
  runFunction: RunFunction<T>,
  attributes: Record<string, string | number> = {}
): Promise<T> => {
  return await Sentry.startSpan({ name, op, attributes }, runFunction);
};

// Request cookies and these headers carry credentials: Supabase session
// tokens, the login-as marker, API keys. Sentry's request data integration
// attaches cookies and headers to events by default.
const CREDENTIAL_HEADERS = new Set([
  "cookie",
  "set-cookie",
  "authorization",
  "x-api-key",
]);

export function removeRequestCredentials<T extends Sentry.Event>(event: T): T {
  const request = event.request;
  if (!request) return event;
  delete request.cookies;
  if (request.headers) {
    request.headers = Object.fromEntries(
      Object.entries(request.headers).filter(
        ([name]) => !CREDENTIAL_HEADERS.has(name.toLowerCase())
      )
    );
  }
  return event;
}

export function cleanupSentryTransactionEvent(
  event: TransactionEvent
): TransactionEvent {
  return removeRequestCredentials(event);
}

export function cleanupSentryErrorEvent(
  event: Sentry.ErrorEvent,
  hint: Sentry.EventHint
) {
  removeRequestCredentials(event);
  attachObjectTags(event, hint);
  removeSentryWrapperStacktrace(event);
  fixObjectCapturedAsExceptionError(event, hint);
  return event;
}

function attachObjectTags(event: Sentry.ErrorEvent, hint: Sentry.EventHint) {
  if (
    hint.originalException &&
    typeof hint.originalException === "object" &&
    "objectIdentifier" in hint.originalException &&
    typeof hint.originalException.objectIdentifier === "string" &&
    "objectType" in hint.originalException &&
    typeof hint.originalException.objectType === "string"
  ) {
    event.tags = {
      ...event.tags,
      [hint.originalException.objectType]:
        hint.originalException.objectIdentifier,
    };
  }
}

function fixObjectCapturedAsExceptionError(
  event: Sentry.ErrorEvent,
  hint: Sentry.EventHint
) {
  const exception = event.exception?.values?.[0];
  const original = hint?.originalException;

  if (
    exception &&
    original &&
    typeof original === "object" &&
    "message" in original &&
    !("stack" in original)
  ) {
    exception.value = String(original.message);

    if (
      "code" in original &&
      typeof original.code === "string" &&
      original.code.startsWith("PGRST")
    ) {
      exception.type = "PostgresError";
    } else {
      exception.type = exception.type || "Error";
    }

    exception.mechanism = {
      type: "generic",
      handled: true,
    };
  }
}

function removeSentryWrapperStacktrace(event: Sentry.ErrorEvent) {
  const stacktrace = event.exception?.values?.[0]?.stacktrace;
  const lastFrame = stacktrace?.frames?.[stacktrace?.frames?.length - 1];
  if (lastFrame?.function === "logException") {
    event.exception!.values![0].stacktrace!.frames?.pop();
  }
}

/**
 * The `beforeSend` / `beforeSendTransaction` pair every `Sentry.init` spreads
 * in: both remove request credentials (see `removeRequestCredentials`), and
 * error events are also cleaned up for grouping.
 */
export const sentryEventHooks = {
  beforeSend: cleanupSentryErrorEvent,
  beforeSendTransaction: cleanupSentryTransactionEvent,
};
